import axios from "axios";
import { ERROR_MSGS } from "../constants/constants";

const apiUrl = process.env.REACT_APP_API_URL;
const AUTH_ROLE = "INVESTOR";
// Refresh before the access cookie expires (API access TTL is 30 minutes).
const KEEP_ALIVE_INTERVAL_MS = 20 * 60 * 1000;

const isSessionRevokedError = (err) => {
  const raw =
    err?.response?.data?.error?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.response ||
    err?.message ||
    "";
  const text = typeof raw === "string" ? raw : raw?.message || "";
  return /session revoked/i.test(text);
};

const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;
let keepAliveTimer = null;

const isRefreshRequest = (config) => {
  const url = config?.url || "";
  return url.includes("/api/auth/refresh");
};

const getErrorMessage = (error) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.response?.data?.response ||
  "An error occurred";

export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${apiUrl}/api/auth/refresh`, { role: AUTH_ROLE }, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const onVisibilityChange = () => {
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    refreshSession().catch(() => {});
  }
};

export const startSessionKeepAlive = () => {
  if (typeof window === "undefined" || keepAliveTimer) return;
  refreshSession().catch(() => {});
  keepAliveTimer = setInterval(() => {
    refreshSession().catch(() => {});
  }, KEEP_ALIVE_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
};

export const stopSessionKeepAlive = () => {
  if (keepAliveTimer) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
};

const forceLogout = (sessionRevoked = false) => {
  stopSessionKeepAlive();
  localStorage.clear();
  sessionStorage.clear();
  if (sessionRevoked) {
    sessionStorage.setItem(
      "sessionRevokedMessage",
      ERROR_MSGS.SESSION_ELSEWHERE
    );
  }
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      if (isRefreshRequest(originalRequest)) {
        forceLogout(isSessionRevokedError(error));
        return Promise.reject(error);
      }

      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          await refreshSession();
          return api(originalRequest);
        } catch (err) {
          forceLogout(isSessionRevokedError(err));
          return Promise.reject(err);
        }
      }
    }

    let errorMessage = getErrorMessage(error);

    if ((!errorMessage || errorMessage === "An error occurred") && error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        errorMessage = parsed.error || parsed.message || parsed.response || errorMessage;
      } catch {
        // keep fallback
      }
    }

    return Promise.reject(errorMessage || "An error occurred");
  }
);

export default api;
